import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useBreakpoint, useDesktopScale } from '../layout/BreakpointProvider';
import { buttonReset, color, font, FONT_FAMILY, radius, space, TAP, weight } from '../theme';
import { Badge } from './Badge';
import { ButtonLink } from './Button';
import Icon from './Icon';
import LivePlayer from './LivePlayer';

/* The in-app player (decision 1A): a bottom sheet on phone and tablet, a centred dialog on
   desktop, over the page. One stream at a time; the same component plays live broadcasts
   (`live`: the red "בשידור חי" badge) and recordings (bill timeline, past sessions).
   Renders nothing while closed, so hls.js loads only when it first opens and the stream
   stops when it closes.
   Accessibility: a modal dialog; focus moves to the close button on open, Tab stays inside,
   Escape and the backdrop close it, and focus returns to the button that opened it. The page
   behind doesn't scroll while it's open. */

export interface PlayerSpeaker {
  name: string;
  faction?: string | null;
}

interface PlayerDialogProps {
  open: boolean;
  onClose: () => void;
  src: string;                    // the HLS playlist (.m3u8)
  title: string;                  // e.g. 'ישיבת המליאה', the committee's name
  subtitle?: string | null;       // a short meta line, e.g. 'מאז 11:00' or a recording's date
  live?: boolean;                 // a live broadcast: the red "בשידור חי" badge
  paused?: boolean;               // a live broadcast on a break: "בהפסקה" instead
  poster?: string | null;
  speaker?: PlayerSpeaker | null; // "מדבר/ת עכשיו: name · faction"
  agenda?: string | null;         // "על סדר היום: …"
  externalUrl?: string | null;    // the session's page on the Knesset site (also the fallback when the stream can't play)
  externalLabel?: string;
}

const FOCUSABLE = 'a[href], button:not([disabled]), video[controls], [tabindex]:not([tabindex="-1"])';

const StatusBadge = ({ live, paused }: { live: boolean; paused: boolean }) => {
  if (live && paused) {
    return <Badge><span style={{ display: 'inline-flex', alignItems: 'center', gap: space.xs }}><Icon name="pause" size={12} />בהפסקה</span></Badge>;
  }
  if (live) return <Badge tone="danger" dot={color.live}>בשידור חי</Badge>;
  return <Badge>הקלטה</Badge>;
};

const SpeakerLine = ({ speaker, large }: { speaker: PlayerSpeaker; large: boolean }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: space.sm + 2, padding: `${space.sm}px ${space.sm + 2}px`, borderRadius: radius.md + 2, background: color.surfaceMuted }}>
    <span aria-hidden="true" style={{
      width: large ? 40 : 34, height: large ? 40 : 34, borderRadius: '50%', flexShrink: 0, background: color.primarySoft, color: color.primary,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Icon name="mic" size={large ? 20 : 18} />
    </span>
    <span style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
      <span style={{ fontSize: font.caption, color: color.textSecondary }}>מדבר/ת עכשיו</span>
      <span style={{ fontSize: font.label, fontWeight: weight.bold, color: color.text }}>
        {speaker.name}
        {speaker.faction && <span style={{ fontWeight: weight.regular, color: color.textSecondary }}> · {speaker.faction}</span>}
      </span>
    </span>
  </div>
);

const PlayerDialog = ({
  open, onClose, src, title, subtitle, live = false, paused = false, poster, speaker, agenda, externalUrl,
  externalLabel = 'דף הישיבה באתר הכנסת',
}: PlayerDialogProps) => {
  const { isDesktop, isTablet } = useBreakpoint();
  const scale = useDesktopScale();
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  // The latest onClose, so an inline handler doesn't re-run the open/close effect
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const body = document.body;
    const prevOverflow = body.style.overflow;
    body.style.overflow = 'hidden';
    if (closeRef.current) closeRef.current.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      // Keep Tab inside the dialog
      const items = Array.prototype.slice.call(panelRef.current.querySelectorAll(FOCUSABLE)) as HTMLElement[];
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !panelRef.current.contains(active))) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (active === last || !panelRef.current.contains(active))) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      body.style.overflow = prevOverflow;
      if (opener && document.contains(opener)) opener.focus();
    };
  }, [open]);

  if (!open) return null;

  const closeButton = (
    <button
      ref={closeRef}
      type="button"
      onClick={onClose}
      aria-label="סגירת הנגן"
      style={{
        ...buttonReset, width: TAP, height: TAP, marginInlineEnd: -space.sm, borderRadius: '50%', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center', color: color.textSecondary,
      }}
    >
      <Icon name="close" size={22} strokeWidth={2} />
    </button>
  );

  const player = (
    <LivePlayer key={src} src={src} poster={poster} live={live} title={title} fallbackUrl={externalUrl} />
  );

  const details = (large: boolean) => (
    <>
      {speaker && <SpeakerLine speaker={speaker} large={large} />}
      {agenda && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
          <span style={{ fontSize: font.small, fontWeight: weight.bold, color: color.textSecondary }}>על סדר היום</span>
          <span style={{ fontSize: large ? font.body : font.small, color: color.text, lineHeight: 1.5 }}>{agenda}</span>
        </div>
      )}
      {externalUrl && (
        <div style={large ? { borderTop: `1px solid ${color.divider}`, paddingTop: space.md, display: 'flex', flexDirection: 'column', gap: space.sm, alignItems: 'flex-start' } : undefined}>
          <ButtonLink href={externalUrl} icon="external" variant="neutral">{externalLabel}</ButtonLink>
          {large && <span style={{ fontSize: font.caption, color: color.textTertiary }}>{live ? 'השידור נטען' : 'ההקלטה נטענת'} מהכנסת ישירות לנגן באתר.</span>}
        </div>
      )}
    </>
  );

  const scrim = (
    <div aria-hidden="true" onClick={onClose} style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, background: 'rgba(16,24,40,0.45)' }} />
  );

  const root: React.CSSProperties = {
    position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, zIndex: 1000,
    direction: 'rtl', fontFamily: FONT_FAMILY, color: color.text,
  };

  // Desktop: a centred dialog, scaled like the page area (layout/constants)
  if (isDesktop) {
    const z = scale.content;
    return createPortal(
      <div style={{ ...root, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {scrim}
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          style={{
            position: 'relative', zoom: z, boxSizing: 'border-box',
            width: `min(1000px, calc((100vw - 64px) / ${z}))`, maxHeight: `calc((100vh - 64px) / ${z})`, overflowY: 'auto',
            padding: `${space.xl}px ${space.xxl}px ${space.xxl}px`, background: color.surface, borderRadius: 22,
            boxShadow: '0 24px 64px rgba(16,24,40,0.28)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: space.md, marginBottom: space.md + 2 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: space.sm + 2, minWidth: 0, flexWrap: 'wrap' }}>
              <StatusBadge live={live} paused={paused} />
              <h2 id={titleId} style={{ margin: 0, fontSize: 20, fontWeight: weight.bold }}>{title}</h2>
              {subtitle && <span style={{ fontSize: font.small, color: color.textSecondary }}>{subtitle}</span>}
            </span>
            {closeButton}
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: space.xl }}>
            <div style={{ flex: '0 0 64%', minWidth: 0 }}>{player}</div>
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: space.md }}>{details(true)}</div>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  // Phone and tablet: a bottom sheet (tablet: as wide as the page column)
  return createPortal(
    <div style={root}>
      {scrim}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, margin: '0 auto', maxWidth: isTablet ? 720 : undefined,
          boxSizing: 'border-box', maxHeight: '92vh', overflowY: 'auto',
          padding: `${space.sm}px ${space.lg}px calc(${space.xxl}px + env(safe-area-inset-bottom))`,
          background: color.surface, borderRadius: '22px 22px 0 0', boxShadow: '0 -8px 24px rgba(16,24,40,0.18)',
        }}
      >
        <div aria-hidden="true" style={{ width: 40, height: 5, borderRadius: 3, background: color.track, margin: `0 auto ${space.xs}px` }} />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: space.sm }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: space.sm, minWidth: 0 }}>
            <StatusBadge live={live} paused={paused} />
            {subtitle && <span style={{ fontSize: font.small, color: color.textSecondary }}>{subtitle}</span>}
          </span>
          {closeButton}
        </div>
        <div style={{ marginTop: space.xs }}>{player}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: space.sm, marginTop: space.md }}>
          <h2 id={titleId} style={{ margin: 0, fontSize: font.heading, fontWeight: weight.bold, lineHeight: 1.35 }}>{title}</h2>
          {details(false)}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default PlayerDialog;
